/**
 * Crypto wallet vault. BIP-39 seed phrase intake -> Shamir 3-of-5 split.
 * v0 stores ONLY encrypted shares, not the raw seed. The seed is never
 * persisted in plaintext form; the user pastes it once, we split + wrap +
 * discard the original buffer (overwritten before GC).
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, CHAINS, formatINR, toPaise } from '../types.ts';
import type { Chain, CryptoSeed, NomineeId, SeedId } from '../types.ts';
import { split } from '../chassis/shamir.ts';
import { wrapShare } from '../chassis/crypto.ts';

const TPL = /* html */ `
<section class="frame">
  <h2>Crypto wallet vault <span class="deva">क्रिप्टो</span></h2>
  <div class="kicker">Seed phrase split via Shamir 3-of-5 · raw seed never persisted</div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <div class="row">
      <label class="field">Wallet name
        <input id="name" type="text" required placeholder="Cold storage Ledger" />
      </label>
      <label class="field">Chain
        <select id="chain">
          ${CHAINS.map((c) => `<option value="${c}">${c}</option>`).join('')}
        </select>
      </label>
    </div>
    <div class="row">
      <label class="field">Hint
        <input id="hint" type="text" placeholder="e.g. ledger nano under the puja shelf" />
      </label>
      <label class="field">Estimated value (₹)
        <input id="val" type="number" min="0" step="1000" placeholder="500000" />
      </label>
    </div>
    <label class="field">BIP-39 seed phrase (12 or 24 words)
      <textarea id="seed" rows="3" placeholder="word1 word2 word3 ... ·  cleared from memory after split"></textarea>
    </label>
    <div class="btn-row">
      <button type="submit" class="btn">Split & store</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatCryptoVault extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      if (!list) return;
      if (v.crypto_seeds.length === 0) {
        list.innerHTML = `<div class="list-empty">No wallets sharded yet.</div>`;
        return;
      }
      list.innerHTML = v.crypto_seeds.map((s) => {
        const status = s.shamir_shares_encrypted.length === 5 ? 'leaf' : 'crimson';
        return `<div class="list-row ${status}">
          <div>
            <div class="title">${escapeHtml(s.wallet_name)} <span class="pill indigo">${escapeHtml(s.chain)}</span></div>
            <div class="sub">${s.shamir_threshold}-of-${s.shamir_share_count} · ${s.shamir_shares_encrypted.length} shares stored${s.hint ? ' · ' + escapeHtml(s.hint) : ''}</div>
          </div>
          <div style="display:grid;gap:6px;justify-items:end;">
            <div class="meta">${formatINR(s.estimated_value_paise)}</div>
            <button class="btn tiny ghost" data-del="${s.id}">Remove</button>
          </div>
        </div>`;
      }).join('');
      list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
        b.addEventListener('click', () => {
          const id = b.dataset['del'] as SeedId;
          void store.mutate((d) => { d.crypto_seeds = d.crypto_seeds.filter((x) => x.id !== id); });
        });
      });
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = (this.#shadow.getElementById('name') as HTMLInputElement).value.trim();
      const chain = (this.#shadow.getElementById('chain') as HTMLSelectElement).value as Chain;
      const hint = (this.#shadow.getElementById('hint') as HTMLInputElement).value.trim();
      const val = Number((this.#shadow.getElementById('val') as HTMLInputElement).value);
      const seedField = this.#shadow.getElementById('seed') as HTMLTextAreaElement;
      const seed = seedField.value.trim();
      if (!name || !seed) return;
      const words = seed.split(/\s+/);
      if (words.length !== 12 && words.length !== 24) {
        seedField.setAttribute('aria-invalid', 'true');
        return;
      }
      const holders = store.state.vault?.nominees.filter((n) => n.is_shamir_holder) ?? [];
      const total = store.state.vault?.dead_man_switch.shamir_total ?? 5;
      const threshold = store.state.vault?.dead_man_switch.shamir_threshold ?? 3;
      if (holders.length < total) {
        alert(`Need ${total} Shamir-holder nominees first. Currently: ${holders.length}.`);
        return;
      }
      const enc = new TextEncoder().encode(seed);
      const padded = new Uint8Array(Math.max(64, enc.length));
      padded.set(enc);
      const shares = split(padded, threshold, total);
      const wrapped = await Promise.all(
        shares.map((sh, i) => wrapShare(sh, (holders[i]?.id ?? `holder-${i}`) as NomineeId))
      );
      // Wipe sensitive buffers
      enc.fill(0);
      padded.fill(0);
      seedField.value = '';
      const cs: CryptoSeed = {
        id: newId<SeedId>(),
        wallet_name: name,
        chain,
        shamir_share_count: 5,
        shamir_threshold: 3,
        shamir_shares_encrypted: wrapped,
        hint,
        estimated_value_paise: Number.isFinite(val) ? toPaise(val) : 0,
        created_at: new Date().toISOString(),
      };
      await store.mutate((d) => { d.crypto_seeds.push(cs); });
      form.reset();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-crypto-vault', WasiyatCryptoVault);
